import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ChatIcon from "@mui/icons-material/Chat";
import ClearIcon from "@mui/icons-material/Clear";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import WcIcon from "@mui/icons-material/Wc";
import { Box, Skeleton, Stack, Typography, useMediaQuery } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import searchIcon from "../../assets/icons/SearchIcon.svg";
import backArrow from "../../assets/icons/backArrow.svg";
import { CustomIcon, CustomInput } from "../../global/components";
import CustomLoader from "../../global/components/CustomLoader/CustomLoader";
import strings from "../../global/constants/StringConstants";
import urls from "../../global/constants/UrlConstants";
import {
  debounceEventHandler,
  handleErrorMessage,
} from "../../helpers/methods";
import {
  selectChatUsers,
  selectUserEmail,
  setChatUsersAction,
  setSelectedUserAction,
  setTimezoneAction,
} from "../../redux/authSlice";
import history from "../../utils/history";
import { useAppDispatch, useAppSelector } from "../../utils/hooks";
import { LightTooltip, centerItemFlex, theme } from "../../utils/styles";
import PreferencesDialog from "../Shared/Dialogs/PreferenceDialog/PreferenceDialog";
import vedazTalkStyles from "./VedazTalk.styles";
import {
  chatUserList,
  favouriteUserList,
  getDailyProfiles,
  makeFavouriteUserChat,
  matchUserList,
  removeFavouriteUserChat,
} from "./VedazTalkService";

const VedazTalk = () => {
  const classes = vedazTalkStyles;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const loginUser = useAppSelector(selectUserEmail);
  const existingChatUser = useAppSelector(selectChatUsers);
  const [isChatLoading, setIsChatLoading] = useState<boolean>(false);
  const [isMatchLoading, setMatchIsLoading] = useState<boolean>(false);
  const [isFavoriteLoading, setIsFavoriteLoading] = useState<boolean>(false);
  const [matchUsers, setMatchUsers] = useState<any[]>([]);
  const [chatUsers, setChatUsers] = useState<any[]>([]);
  const [favoriteUsers, setFavoriteUsers] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<"all" | "favorites">("all");
  const [matchPage, setMatchPage] = useState<number>(1);
  const [chatPage, setChatPage] = useState<number>(1);
  const [favoritePage, setFavoritePage] = useState<number>(1);
  const [matchCount, setMatchCount] = useState<number>(0);
  const [chatCount, setChatCount] = useState<number>(0);
  const [favoriteCount, setFavoriteCount] = useState<number>(0);
  const [matchRowsPerPage, setMatchRowsPerPage] = useState<number>(6);
  const [chatRowsPerPage, setChatRowsPerPage] = useState<number>(6);
  const [favoriteRowsPerPage, setFavoriteRowsPerPage] = useState<number>(6);
  const [matchSearch, setMatchSearch] = useState<string>("");
  const [chatSearch, setChatSearch] = useState<string>("");
  const [favoriteSearch, setFavoriteSearch] = useState<string>("");
  const [openPreference, setOpenPreference] = useState<boolean>(false);
  const [updatePreference, setUpdatePreference] = useState<boolean>(false);
  const [isPreferenceLoading, setIsPreferenceLoading] =
    useState<boolean>(false);
  const [togglingFavorite, setTogglingFavorite] = useState<string | null>(null);
  const [dailyProfiles, setDailyProfiles] = useState<any[]>([]);
  const [isDailyLoading, setIsDailyLoading] = useState<boolean>(false);

  useEffect(() => {
    getDailyProfilesData();
  }, []);

  useEffect(() => {
    setMatchCount(0);
    getMatchUsersData();
  }, [matchPage, matchRowsPerPage, matchSearch, updatePreference]);

  useEffect(() => {
    setChatCount(0);
    getChatUsersData();
  }, [chatPage, chatRowsPerPage, chatSearch]);

  useEffect(() => {
    setFavoriteCount(0);
    getFavoriteUsersData();
  }, [favoritePage, favoriteRowsPerPage, favoriteSearch]);

  const getMatchUsersData = async () => {
    try {
      setMatchIsLoading(true);
      const matchUsersRes = await matchUserList(
        loginUser,
        matchPage,
        matchRowsPerPage,
        matchSearch
      );
      convertDataForMatchUserTable(matchUsersRes.data);
      setMatchCount(matchUsersRes.pagination.totalItems);
      setMatchIsLoading(false);
    } catch (error: any) {
      setMatchIsLoading(false);
      handleErrorMessage(error);
    }
  };

  const getChatUsersData = async () => {
    try {
      setIsChatLoading(true);
      const chatUsersRes = await chatUserList(
        loginUser,
        chatPage,
        chatRowsPerPage,
        chatSearch
      );
      convertDataForChatUserTable(chatUsersRes.data);
      dispatch(setTimezoneAction({ timezone: chatUsersRes.timezone }));
      setChatCount(chatUsersRes.pagination.totalItems);
      setIsChatLoading(false);
    } catch (error: any) {
      setIsChatLoading(false);
      handleErrorMessage(error);
    }
  };

  const getFavoriteUsersData = async () => {
    try {
      setIsFavoriteLoading(true);
      const favoriteUsersRes = await favouriteUserList(
        loginUser,
        favoritePage,
        favoriteRowsPerPage,
        favoriteSearch
      );
      convertDataForFavoriteUserTable(favoriteUsersRes.data);
      setFavoriteCount(favoriteUsersRes.pagination.totalItems);
      setIsFavoriteLoading(false);
    } catch (error: any) {
      setIsFavoriteLoading(false);
      handleErrorMessage(error);
    }
  };

  const renderActionCell = (item: any) => {
    return (
      <Box
        key={item.username}
        sx={{ cursor: "pointer" }}
        onClick={() => handleChatSelection(item)}
      >
        <ChatIcon htmlColor="#8876F0" />
      </Box>
    );
  };

  const convertDataForMatchUserTable = (data: any) => {
    const tableValue = data.map((item: any) => ({
      firstName: item.firstName,
      lastName: item.lastName,
      name: item.firstName,
      percentage: item.percentage,
      gender: item.gender,
      username: item.username,
      nickname: item.nickname,
      action: renderActionCell(item),
    }));
    setMatchUsers(tableValue);
  };

  const convertDataForChatUserTable = (data: any) => {
    const tableValue = data.map((item: any) => ({
      firstName: item.firstName,
      lastName: item.lastName,
      name: item.firstName,
      percentage: item.percentage,
      gender: item.gender,
      username: item.username,
      nickname: item.nickname,
      unreadCount: item.unreadCount,
      favourite: item.favourite || false,
      action: renderActionCell(item),
    }));
    setChatUsers(tableValue);
    dispatch(setChatUsersAction({ chatUsers: tableValue }));
  };

  const convertDataForFavoriteUserTable = (data: any) => {
    const tableValue = data.map((item: any) => ({
      firstName: item.firstName,
      lastName: item.lastName,
      name: item.firstName,
      percentage: item.percentage,
      gender: item.gender,
      username: item.username,
      nickname: item.nickname,
      unreadCount: item.unreadCount,
      favourite: true,
      action: renderActionCell(item),
    }));
    setFavoriteUsers(tableValue);
  };

  const handleChatSelection = (user: any) => {
    const isExistingChat = existingChatUser.some(
      (chatUser: any) => chatUser.username === user.username
    );
    if (!isExistingChat) {
      const updatedChatUsers = [user, ...existingChatUser];
      dispatch(setChatUsersAction({ chatUsers: updatedChatUsers }));
    }
    dispatch(setSelectedUserAction({ selectedUser: user }));
    history.push(urls.vedazChatViewPath);
  };

  const handleFavoriteToggle = async (user: any, favourite: boolean) => {
    if (togglingFavorite === user.username) return;

    setTogglingFavorite(user.username);

    try {
      if (favourite) {
        await removeFavouriteUserChat(loginUser, user.username);

        setFavoriteCount((prev) => Math.max(0, prev - 1));

        if (activeTab === "all") {
          const updatedChatUsers: any[] = chatUsers.map((chatUser: any) =>
            chatUser.username === user.username
              ? { ...chatUser, favourite: false }
              : chatUser
          );
          setChatUsers(updatedChatUsers);
          dispatch(setChatUsersAction({ chatUsers: updatedChatUsers }));
        } else {
          const updatedFavoriteUsers: any[] = favoriteUsers.filter(
            (favUser: any) => favUser.username !== user.username
          );
          setFavoriteUsers(updatedFavoriteUsers);

          const updatedChatUsers: any[] = chatUsers.map((chatUser: any) =>
            chatUser.username === user.username
              ? { ...chatUser, favourite: false }
              : chatUser
          );
          setChatUsers(updatedChatUsers);
          dispatch(setChatUsersAction({ chatUsers: updatedChatUsers }));
        }
      } else {
        await makeFavouriteUserChat(loginUser, user.username);

        setFavoriteCount((prev) => prev + 1);

        const updatedChatUsers: any[] = chatUsers.map((chatUser: any) =>
          chatUser.username === user.username
            ? { ...chatUser, favourite: true }
            : chatUser
        );
        setChatUsers(updatedChatUsers);
        dispatch(setChatUsersAction({ chatUsers: updatedChatUsers }));

        getFavoriteUsersData();
      }
    } catch (error: any) {
      handleErrorMessage(error);
      getChatUsersData();
      getFavoriteUsersData();
    } finally {
      setTogglingFavorite(null);
    }
  };

  const handlePreferenceChange = () => {
    setChatPage(1);
    setChatRowsPerPage(6);
    setMatchPage(1);
    setMatchRowsPerPage(6);
    setUpdatePreference(!updatePreference);
  };

  const handleMatchPageChange = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setMatchPage(newPage);
  };

  const handleChatPageChange = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setChatPage(newPage);
  };

  const handleFavoritePageChange = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setFavoritePage(newPage);
  };

  const handleMatchSearchChange = (e: any) => {
    const value = e.target.value;
    setMatchSearch(value);
  };

  const handleMatchSearch = (e: any) => {
    setMatchPage(1);
  };

  const handleChatSearchChange = (e: any) => {
    const value = e.target.value;
    setChatSearch(value);
  };

  const handleChatSearch = (e: any) => {
    setChatPage(1);
  };

  const handleFavoriteSearchChange = (e: any) => {
    const value = e.target.value;
    setFavoriteSearch(value);
  };

  const handleFavoriteSearch = (e: any) => {
    setFavoritePage(1);
  };

  const handleClearSearch = () => {
    if (activeTab === "all") {
      setChatSearch("");
      setChatPage(1);
    } else {
      setFavoriteSearch("");
      setFavoritePage(1);
    }
  };

  const handleClearMatchSearch = () => {
    setMatchSearch("");
    setMatchPage(1);
  };

  const getDailyProfilesData = async () => {
    try {
      setIsDailyLoading(true);
      const res = await getDailyProfiles(loginUser);
      setDailyProfiles(res.data ?? []);
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsDailyLoading(false);
    }
  };

  const handleTabChange = (tab: "all" | "favorites") => {
    setActiveTab(tab);

    // Clear search and reset page when switching tabs
    // The useEffect will automatically fetch data when search/page changes
    if (tab === "favorites") {
      setFavoriteSearch("");
      setFavoritePage(1);
    } else {
      setChatSearch("");
      setChatPage(1);
    }
  };

  const handleLoading = (loading: boolean) => {
    setIsChatLoading(loading);
    setMatchIsLoading(loading);
  };

  const getHeader = () => {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { sm: "center", xs: "baseline" },
          width: "100%",
        }}
        pb={3}
      >
        <Box
          display={"flex"}
          alignItems={"center"}
          sx={{ cursor: "pointer", width: "fit-content" }}
          onClick={() => history.goBack()}
        >
          {isMobile ? (
            <img src={backArrow} height={"20px"} alt="Module not found..." />
          ) : (
            <img src={backArrow} height={"25px"} alt="Module not found..." />
          )}
        </Box>
        <Typography sx={classes.italicTypo}>{t("vedazHeader")}</Typography>
        <Box
          gap={1}
          onClick={() => setOpenPreference(true)}
          sx={{ ...centerItemFlex, cursor: "pointer" }}
        >
          <CustomIcon icon={<WcIcon htmlColor="#ffffff" />} />
          <Typography sx={classes.username}>{t("preference")}</Typography>
        </Box>
      </Box>
    );
  };

  const getSkeletonCard = () => {
    return (
      <Box sx={classes.userBox}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Skeleton
            variant="rectangular"
            width="40%"
            height={24}
            sx={{ bgcolor: "rgba(255, 255, 255, 0.1)", borderRadius: "4px" }}
          />
          <Skeleton
            variant="rectangular"
            width="20%"
            height={24}
            sx={{ bgcolor: "rgba(255, 255, 255, 0.1)", borderRadius: "4px" }}
          />
        </Box>
        <Skeleton
          variant="rectangular"
          width="60%"
          height={20}
          sx={{
            mb: 2,
            bgcolor: "rgba(255, 255, 255, 0.1)",
            borderRadius: "4px",
          }}
        />
        <Box sx={classes.genderBox}>
          <Skeleton
            variant="rectangular"
            width={40}
            height={24}
            sx={{ bgcolor: "rgba(255, 255, 255, 0.1)", borderRadius: "4px" }}
          />
          <Skeleton
            variant="rectangular"
            width={28}
            height={28}
            sx={{ bgcolor: "rgba(255, 255, 255, 0.1)", borderRadius: "4px" }}
          />
        </Box>
      </Box>
    );
  };

  const getTabComponent = () => {
    return (
      <Box sx={classes.tabContainer}>
        <Box
          sx={{
            ...classes.tabButton,
            backgroundColor:
              activeTab === "all" ? "#8876F0" : "rgba(136, 118, 240, 0.1)",
            color: activeTab === "all" ? "white" : "#8876F0",
          }}
          onClick={() => handleTabChange("all")}
        >
          <ChatIcon sx={{ mr: 1, fontSize: "20px" }} />
          <Typography sx={classes.tabText}>
            {t("allUsers")} ({chatCount})
          </Typography>
        </Box>
        <Box
          sx={{
            ...classes.tabButton,
            backgroundColor:
              activeTab === "favorites"
                ? "#8876F0"
                : "rgba(136, 118, 240, 0.1)",
            color: activeTab === "favorites" ? "white" : "#8876F0",
          }}
          onClick={() => handleTabChange("favorites")}
        >
          <FavoriteIcon sx={{ mr: 1, fontSize: "20px" }} />
          <Typography sx={classes.tabText}>
            {t("favorites")} ({favoriteCount})
          </Typography>
        </Box>
      </Box>
    );
  };

  const getChatUserTable = () => {
    const currentUsers = activeTab === "all" ? chatUsers : favoriteUsers;
    const currentCount = activeTab === "all" ? chatCount : favoriteCount;
    const currentLoading =
      activeTab === "all" ? isChatLoading : isFavoriteLoading;
    const currentSearch = activeTab === "all" ? chatSearch : favoriteSearch;
    const currentSearchChangeHandler =
      activeTab === "all" ? handleChatSearchChange : handleFavoriteSearchChange;
    const currentSearchHandler =
      activeTab === "all" ? handleChatSearch : handleFavoriteSearch;
    const currentRowsPerPage =
      activeTab === "all" ? chatRowsPerPage : favoriteRowsPerPage;
    const setCurrentRowsPerPage =
      activeTab === "all" ? setChatRowsPerPage : setFavoriteRowsPerPage;
    const currentPage = activeTab === "all" ? chatPage : favoritePage;
    const currentPageHandler =
      activeTab === "all" ? handleChatPageChange : handleFavoritePageChange;

    return (
      <Box sx={{ mb: 4 }}>
        {getTabComponent()}
        <Box sx={classes.conversationHeaderBox}>
          <Typography variant="h6" sx={classes.conversationHeaderText}>
            {activeTab === "all" ? "💬" : "⭐"}{" "}
            {activeTab === "all" ? t("activeConversation") : t("favoriteUsers")}{" "}
            ({currentCount})
          </Typography>
          <CustomInput
            key={activeTab}
            value={currentSearch}
            onChange={(e: any) => {
              currentSearchChangeHandler(e);
              debounceEventHandler(
                currentSearchHandler,
                strings.SEARCH_TIME_OUT
              )(e);
            }}
            placeHolder={t("searchProfile")}
            customClasses={classes.searchField}
            InputProps={{
              endAdornment: (
                <Stack direction="row" spacing={1} alignItems="center" mr={1}>
                  {currentSearch && (
                    <Box
                      onClick={handleClearSearch}
                      sx={{
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        "&:hover": { opacity: 0.7 },
                      }}
                    >
                      <ClearIcon sx={{ color: "#8876F0", fontSize: "20px" }} />
                    </Box>
                  )}
                  <img src={searchIcon} width="30px" height="20px" />
                </Stack>
              ),
            }}
          />
        </Box>
        <Box sx={classes.conversationContainer}>
          {currentLoading ? (
            Array.from({ length: 6 }).map((_, index) => (
              <React.Fragment key={`chat-skeleton-${index}`}>
                {getSkeletonCard()}
              </React.Fragment>
            ))
          ) : currentUsers.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "40px",
                backgroundColor: "rgba(136, 118, 240, 0.05)",
                borderRadius: "16px",
                width: "100%",
              }}
            >
              {activeTab === "all" ? (
                <ChatIcon
                  sx={{
                    color: "#8876F0",
                    fontSize: "48px",
                    opacity: 0.5,
                    mb: 2,
                  }}
                />
              ) : (
                <FavoriteIcon
                  sx={{
                    color: "#8876F0",
                    fontSize: "48px",
                    opacity: 0.5,
                    mb: 2,
                  }}
                />
              )}
              <Typography sx={classes.conversationHeaderText}>
                {currentSearch
                  ? activeTab === "all"
                    ? t("noChatConversationSearch")
                    : t("noFavoriteUsersSearch")
                  : activeTab === "all"
                  ? t("noChatConversation")
                  : t("noFavoriteUsers")}
              </Typography>
              <Typography sx={{ ...classes.nameText, textAlign: "center" }}>
                {currentSearch
                  ? activeTab === "all"
                    ? t("noChatConversationSearchSub")
                    : t("noFavoriteUsersSearchSub")
                  : activeTab === "all"
                  ? t("noChatConversationSub")
                  : t("noFavoriteUsersSub")}
              </Typography>
            </Box>
          ) : (
            currentUsers.slice(0, currentRowsPerPage).map((user: any) => (
              <Box key={user.username} sx={classes.userBox}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Typography
                    sx={{
                      ...classes.nicknameText,
                      [theme.breakpoints.down("sm")]: {
                        maxWidth: "280px",
                      },
                    }}
                  >
                    {user.nickname}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    {user.unreadCount > 0 && (
                      <Box sx={classes.unreadCount}>
                        {user.unreadCount > 99 ? "99+" : user.unreadCount}
                      </Box>
                    )}
                    {activeTab === "all" && (
                      <LightTooltip
                        arrow
                        title={
                          user.favourite
                            ? t("removeFromFavorites")
                            : t("addToFavorites")
                        }
                        placement="top"
                        sx={{
                          "& .MuiTooltip-tooltip": {
                            backgroundColor: "rgba(0, 0, 0, 0.8)",
                            fontSize: "14px",
                            color: "#ffffff",
                            borderRadius: "8px",
                            padding: "8px 12px",
                            backdropFilter: "blur(10px)",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            ...classes.favoriteIconContainer,
                            opacity:
                              togglingFavorite === user.username ? 0.5 : 1,
                            cursor:
                              togglingFavorite === user.username
                                ? "not-allowed"
                                : "pointer",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (togglingFavorite !== user.username) {
                              handleFavoriteToggle(user, user.favourite);
                            }
                          }}
                        >
                          {user.favourite ? (
                            <FavoriteIcon sx={classes.favoriteIconFilled} />
                          ) : (
                            <FavoriteBorderIcon
                              sx={classes.favoriteIconOutline}
                            />
                          )}
                        </Box>
                      </LightTooltip>
                    )}
                    {activeTab === "favorites" && (
                      <LightTooltip
                        arrow
                        title={t("removeFromFavorites")}
                        placement="top"
                        sx={{
                          "& .MuiTooltip-tooltip": {
                            backgroundColor: "rgba(0, 0, 0, 0.8)",
                            fontSize: "14px",
                            color: "#ffffff",
                            borderRadius: "8px",
                            padding: "8px 12px",
                            backdropFilter: "blur(10px)",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            ...classes.favoriteIconContainer,
                            backgroundColor: "rgba(255, 107, 107, 0.2)",
                            borderColor: "rgba(255, 107, 107, 0.4)",
                            opacity:
                              togglingFavorite === user.username ? 0.5 : 1,
                            cursor:
                              togglingFavorite === user.username
                                ? "not-allowed"
                                : "pointer",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (togglingFavorite !== user.username) {
                              handleFavoriteToggle(user, true);
                            }
                          }}
                        >
                          <FavoriteIcon sx={classes.favoriteIconFilled} />
                        </Box>
                      </LightTooltip>
                    )}
                  </Box>
                </Box>
                <Typography sx={classes.nameText}>{user.name}</Typography>
                <Box sx={classes.genderBox}>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Typography
                      sx={{
                        ...classes.genderText,
                        backgroundColor:
                          user.gender === 1 ? "#2986cc" : "#c90076",
                      }}
                    >
                      {user.gender === 1 ? "M" : "F"}
                    </Typography>
                    <Typography sx={classes.genderText}>
                      {user.percentage}% Match
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <LightTooltip
                      arrow
                      title={t("compatibility")}
                      placement="top"
                      sx={{
                        "& .MuiTooltip-tooltip": {
                          backgroundColor: "#ffffff",
                          fontSize: "20px",
                          color: "#000000",
                        },
                      }}
                    >
                      <Box
                        sx={{ cursor: "pointer" }}
                        onClick={() => {
                          dispatch(
                            setSelectedUserAction({ selectedUser: user })
                          );
                          history.push(urls.vedazCompatibilityViewPath);
                        }}
                      >
                        <WcIcon sx={classes.chatIcon} />
                      </Box>
                    </LightTooltip>
                    <LightTooltip
                      arrow
                      title={t("chat")}
                      placement="top"
                      sx={{
                        "& .MuiTooltip-tooltip": {
                          backgroundColor: "#ffffff",
                          fontSize: "20px",
                          color: "#000000",
                        },
                      }}
                    >
                      <Box
                        sx={{ cursor: "pointer" }}
                        onClick={() => handleChatSelection(user)}
                      >
                        <ChatIcon sx={classes.chatIcon} />
                      </Box>
                    </LightTooltip>
                  </Box>
                </Box>
              </Box>
            ))
          )}
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mt: 3,
            gap: 2,
          }}
        >
          <Box sx={classes.rowPerPageBox}>
            <Typography sx={classes.rowPerPageText}>Rows per page:</Typography>
            <select
              value={currentRowsPerPage}
              onChange={(e) => setCurrentRowsPerPage(Number(e.target.value))}
              style={classes.rowPerPageSelect}
            >
              <option value={3}>3</option>
              <option value={6}>6</option>
              <option value={9}>9</option>
              <option value={12}>12</option>
              <option value={15}>15</option>
            </select>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            {(() => {
              const totalPages = Math.ceil(currentCount / currentRowsPerPage);
              const currentPageNum = currentPage;
              const pageButtons = [];

              pageButtons.push(
                <Box
                  key={1}
                  onClick={() => currentPageHandler(null, 1)}
                  sx={{
                    ...classes.pageButton,
                    backgroundColor:
                      currentPageNum === 1
                        ? "#8876F0"
                        : "rgba(136, 118, 240, 0.1)",
                    color: currentPageNum === 1 ? "white" : "#8876F0",
                    "&:hover": {
                      backgroundColor:
                        currentPageNum === 1
                          ? "#8876F0"
                          : "rgba(136, 118, 240, 0.2)",
                    },
                  }}
                >
                  1
                </Box>
              );

              if (totalPages > 7) {
                if (currentPageNum > 4) {
                  pageButtons.push(
                    <Box
                      key="dots1"
                      sx={{ alignSelf: "flex-end", color: "#8876F0", px: 1 }}
                    >
                      ...
                    </Box>
                  );
                }

                for (
                  let i = Math.max(2, currentPageNum - 2);
                  i <= Math.min(totalPages - 1, currentPageNum + 2);
                  i++
                ) {
                  pageButtons.push(
                    <Box
                      key={i}
                      onClick={() => currentPageHandler(null, i)}
                      sx={{
                        ...classes.pageButton,
                        backgroundColor:
                          currentPageNum === i
                            ? "#8876F0"
                            : "rgba(136, 118, 240, 0.1)",
                        color: currentPageNum === i ? "white" : "#8876F0",
                        "&:hover": {
                          backgroundColor:
                            currentPageNum === i
                              ? "#8876F0"
                              : "rgba(136, 118, 240, 0.2)",
                        },
                      }}
                    >
                      {i}
                    </Box>
                  );
                }

                if (currentPageNum < totalPages - 3) {
                  pageButtons.push(
                    <Box
                      key="dots2"
                      sx={{ alignSelf: "flex-end", color: "#8876F0", px: 1 }}
                    >
                      ...
                    </Box>
                  );
                }

                pageButtons.push(
                  <Box
                    key={totalPages}
                    onClick={() => currentPageHandler(null, totalPages)}
                    sx={{
                      width: "32px",
                      height: "32px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "8px",
                      cursor: "pointer",
                      backgroundColor:
                        currentPageNum === totalPages
                          ? "#8876F0"
                          : "rgba(136, 118, 240, 0.1)",
                      color:
                        currentPageNum === totalPages ? "white" : "#8876F0",
                      "&:hover": {
                        backgroundColor:
                          currentPageNum === totalPages
                            ? "#8876F0"
                            : "rgba(136, 118, 240, 0.2)",
                      },
                    }}
                  >
                    {totalPages}
                  </Box>
                );
              } else {
                for (let i = 2; i <= totalPages; i++) {
                  pageButtons.push(
                    <Box
                      key={i}
                      onClick={() => currentPageHandler(null, i)}
                      sx={{
                        ...classes.pageButton,
                        backgroundColor:
                          currentPageNum === i
                            ? "#8876F0"
                            : "rgba(136, 118, 240, 0.1)",
                        color: currentPageNum === i ? "white" : "#8876F0",
                        "&:hover": {
                          backgroundColor:
                            currentPageNum === i
                              ? "#8876F0"
                              : "rgba(136, 118, 240, 0.2)",
                        },
                      }}
                    >
                      {i}
                    </Box>
                  );
                }
              }

              return pageButtons;
            })()}
          </Box>
        </Box>
      </Box>
    );
  };

  const getMatchUserTable = () => {
    return (
      <Box sx={{ mb: 4 }}>
        <Box sx={classes.conversationHeaderBox}>
          <Typography variant="h6" sx={classes.conversationHeaderText}>
            🔍 {t("compatibleMatches")} ({matchCount})
          </Typography>
          <CustomInput
            value={matchSearch}
            onChange={(e: any) => {
              handleMatchSearchChange(e);
              debounceEventHandler(
                handleMatchSearch,
                strings.SEARCH_TIME_OUT
              )(e);
            }}
            placeHolder={t("searchProfile")}
            customClasses={classes.searchField}
            InputProps={{
              endAdornment: (
                <Stack direction="row" spacing={1} alignItems="center" mr={1}>
                  {matchSearch && (
                    <Box
                      onClick={handleClearMatchSearch}
                      sx={{
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        "&:hover": { opacity: 0.7 },
                      }}
                    >
                      <ClearIcon sx={{ color: "#8876F0", fontSize: "20px" }} />
                    </Box>
                  )}
                  <img src={searchIcon} width="30px" height="20px" />
                </Stack>
              ),
            }}
          />
        </Box>
        <Box sx={classes.conversationContainer}>
          {isMatchLoading ? (
            Array.from({ length: 6 }).map((_, index) => (
              <React.Fragment key={`match-skeleton-${index}`}>
                {getSkeletonCard()}
              </React.Fragment>
            ))
          ) : matchUsers.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "40px",
                backgroundColor: "rgba(136, 118, 240, 0.05)",
                borderRadius: "16px",
                width: "100%",
              }}
            >
              <WcIcon
                sx={{ color: "#8876F0", fontSize: "48px", opacity: 0.5, mb: 2 }}
              />
              <Typography sx={classes.conversationHeaderText}>
                {matchSearch
                  ? t("noMatchConversationSearch")
                  : t("noMatchConversation")}
              </Typography>
              <Typography sx={{ ...classes.nameText, textAlign: "center" }}>
                {matchSearch
                  ? t("noMatchConversationSearchSub")
                  : t("noMatchConversationSub")}
              </Typography>
            </Box>
          ) : (
            matchUsers.slice(0, matchRowsPerPage).map((user: any) => (
              <Box key={user.username} sx={classes.userBox}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Typography sx={classes.nicknameText}>
                    {user.nickname}
                  </Typography>
                </Box>
                <Typography sx={classes.nameText}>{user.name}</Typography>
                <Box sx={classes.genderBox}>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Typography
                      sx={{
                        ...classes.genderText,
                        backgroundColor:
                          user.gender === 1 ? "#2986cc" : "#c90076",
                      }}
                    >
                      {user.gender === 1 ? "M" : "F"}
                    </Typography>
                    <Typography sx={classes.genderText}>
                      {user.percentage}% Match
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <LightTooltip
                      arrow
                      title={t("compatibility")}
                      placement="top"
                      sx={{
                        "& .MuiTooltip-tooltip": {
                          backgroundColor: "#ffffff",
                          fontSize: "20px",
                          color: "#000000",
                        },
                      }}
                    >
                      <Box
                        sx={{ cursor: "pointer" }}
                        onClick={() => {
                          dispatch(
                            setSelectedUserAction({ selectedUser: user })
                          );
                          history.push(urls.vedazCompatibilityViewPath);
                        }}
                      >
                        <WcIcon sx={classes.chatIcon} />
                      </Box>
                    </LightTooltip>
                    <LightTooltip
                      arrow
                      title={t("chat")}
                      placement="top"
                      sx={{
                        "& .MuiTooltip-tooltip": {
                          backgroundColor: "#ffffff",
                          fontSize: "20px",
                          color: "#000000",
                        },
                      }}
                    >
                      <Box
                        sx={{ cursor: "pointer" }}
                        onClick={() => handleChatSelection(user)}
                      >
                        <ChatIcon sx={classes.chatIcon} />
                      </Box>
                    </LightTooltip>
                  </Box>
                </Box>
              </Box>
            ))
          )}
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mt: 3,
            gap: 2,
          }}
        >
          <Box sx={classes.rowPerPageBox}>
            <Typography sx={classes.rowPerPageText}>Rows per page:</Typography>
            <select
              value={matchRowsPerPage}
              onChange={(e) => setMatchRowsPerPage(Number(e.target.value))}
              style={classes.rowPerPageSelect}
            >
              <option value={3}>3</option>
              <option value={6}>6</option>
              <option value={9}>9</option>
              <option value={12}>12</option>
              <option value={15}>15</option>
            </select>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            {(() => {
              const totalPages = Math.ceil(matchCount / matchRowsPerPage);
              const currentPage = matchPage;
              const pageButtons = [];

              pageButtons.push(
                <Box
                  key={1}
                  onClick={() => handleMatchPageChange(null, 1)}
                  sx={{
                    ...classes.pageButton,
                    backgroundColor:
                      currentPage === 1
                        ? "#8876F0"
                        : "rgba(136, 118, 240, 0.1)",
                    color: currentPage === 1 ? "white" : "#8876F0",
                    "&:hover": {
                      backgroundColor:
                        currentPage === 1
                          ? "#8876F0"
                          : "rgba(136, 118, 240, 0.2)",
                    },
                  }}
                >
                  1
                </Box>
              );

              if (totalPages > 7) {
                if (currentPage > 4) {
                  pageButtons.push(
                    <Box
                      key="dots1"
                      sx={{ alignSelf: "flex-end", color: "#8876F0", px: 1 }}
                    >
                      ...
                    </Box>
                  );
                }

                for (
                  let i = Math.max(2, currentPage - 2);
                  i <= Math.min(totalPages - 1, currentPage + 2);
                  i++
                ) {
                  pageButtons.push(
                    <Box
                      key={i}
                      onClick={() => handleMatchPageChange(null, i)}
                      sx={{
                        ...classes.pageButton,
                        backgroundColor:
                          currentPage === i
                            ? "#8876F0"
                            : "rgba(136, 118, 240, 0.1)",
                        color: currentPage === i ? "white" : "#8876F0",
                        "&:hover": {
                          backgroundColor:
                            currentPage === i
                              ? "#8876F0"
                              : "rgba(136, 118, 240, 0.2)",
                        },
                      }}
                    >
                      {i}
                    </Box>
                  );
                }

                if (currentPage < totalPages - 3) {
                  pageButtons.push(
                    <Box
                      key="dots2"
                      sx={{ alignSelf: "flex-end", color: "#8876F0", px: 1 }}
                    >
                      ...
                    </Box>
                  );
                }

                pageButtons.push(
                  <Box
                    key={totalPages}
                    onClick={() => handleMatchPageChange(null, totalPages)}
                    sx={{
                      width: "32px",
                      height: "32px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "8px",
                      cursor: "pointer",
                      backgroundColor:
                        currentPage === totalPages
                          ? "#8876F0"
                          : "rgba(136, 118, 240, 0.1)",
                      color: currentPage === totalPages ? "white" : "#8876F0",
                      "&:hover": {
                        backgroundColor:
                          currentPage === totalPages
                            ? "#8876F0"
                            : "rgba(136, 118, 240, 0.2)",
                      },
                    }}
                  >
                    {totalPages}
                  </Box>
                );
              } else {
                for (let i = 2; i <= totalPages; i++) {
                  pageButtons.push(
                    <Box
                      key={i}
                      onClick={() => handleMatchPageChange(null, i)}
                      sx={{
                        ...classes.pageButton,
                        backgroundColor:
                          currentPage === i
                            ? "#8876F0"
                            : "rgba(136, 118, 240, 0.1)",
                        color: currentPage === i ? "white" : "#8876F0",
                        "&:hover": {
                          backgroundColor:
                            currentPage === i
                              ? "#8876F0"
                              : "rgba(136, 118, 240, 0.2)",
                        },
                      }}
                    >
                      {i}
                    </Box>
                  );
                }
              }

              return pageButtons;
            })()}
          </Box>
        </Box>
      </Box>
    );
  };

  const getDailyRecommendations = () => {
    if (!isDailyLoading && dailyProfiles.length === 0) return null;

    return (
      <Box sx={classes.dailySection}>
        <Box sx={classes.dailySectionHeader}>
          <Box sx={classes.dailyTitleBadge}>
            <AutoAwesomeIcon sx={classes.dailySparkleIcon} />
            <Typography sx={classes.dailySectionTitle}>
              {t("dailyRecommendations")}
            </Typography>
          </Box>
          <Typography sx={classes.dailySectionSubtitle}>
            {t("dailyRecommendationsSubtitle")}
          </Typography>
        </Box>

        <Box sx={classes.dailyGrid}>
          {isDailyLoading
            ? Array.from({ length: 3 }).map((_, i) => (
                <Box key={i} sx={classes.userBox}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                    <Skeleton variant="rectangular" width="50%" height={22} sx={{ bgcolor: "rgba(255,255,255,0.1)", borderRadius: "4px" }} />
                  </Box>
                  <Skeleton variant="rectangular" width="40%" height={18} sx={{ mb: 2, bgcolor: "rgba(255,255,255,0.1)", borderRadius: "4px" }} />
                  <Box sx={classes.genderBox}>
                    <Skeleton variant="rectangular" width={36} height={24} sx={{ bgcolor: "rgba(255,255,255,0.1)", borderRadius: "4px" }} />
                    <Skeleton variant="rectangular" width={28} height={28} sx={{ bgcolor: "rgba(255,255,255,0.1)", borderRadius: "4px" }} />
                  </Box>
                </Box>
              ))
            : dailyProfiles.slice(0, 5).map((user: any) => (
                <Box key={user.username} sx={{ ...classes.userBox, minWidth: 0 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, minWidth: 0 }}>
                    <Typography sx={{ ...classes.nicknameText, maxWidth: "100%" }}>{user.nickname}</Typography>
                  </Box>
                  <Typography sx={classes.nameText}>{user.firstName}</Typography>
                  <Box sx={classes.genderBox}>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Typography
                        sx={{
                          ...classes.genderText,
                          backgroundColor: user.gender === 1 ? "#2986cc" : "#c90076",
                        }}
                      >
                        {user.gender === 1 ? "M" : "F"}
                      </Typography>
                      <Typography sx={classes.genderText}>
                        {user.percentage}% Match
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <LightTooltip
                        arrow
                        title={t("compatibility")}
                        placement="top"
                        sx={{ "& .MuiTooltip-tooltip": { backgroundColor: "#ffffff", fontSize: "20px", color: "#000000" } }}
                      >
                        <Box
                          sx={{ cursor: "pointer" }}
                          onClick={() => {
                            dispatch(setSelectedUserAction({ selectedUser: user }));
                            history.push(urls.vedazCompatibilityViewPath);
                          }}
                        >
                          <WcIcon sx={classes.chatIcon} />
                        </Box>
                      </LightTooltip>
                      <LightTooltip
                        arrow
                        title={t("chat")}
                        placement="top"
                        sx={{ "& .MuiTooltip-tooltip": { backgroundColor: "#ffffff", fontSize: "20px", color: "#000000" } }}
                      >
                        <Box sx={{ cursor: "pointer" }} onClick={() => handleChatSelection(user)}>
                          <ChatIcon sx={classes.chatIcon} />
                        </Box>
                      </LightTooltip>
                    </Box>
                  </Box>
                </Box>
              ))}
        </Box>
      </Box>
    );
  };

  const getVedazTalk = () => {
    return (
      <>
        <Box sx={classes.mainContainer}>
          <Box sx={classes.mainBox}>
            <Box sx={classes.subBox}>
              <Box sx={classes.containerScrollStyle}>
                {getHeader()}
                <Typography sx={classes.noteText} pb={1.5}>
                  {t("note")}: {t("noMatchConversationSub")}
                </Typography>
                {getDailyRecommendations()}
                {getChatUserTable()}
                {getMatchUserTable()}
              </Box>
            </Box>
          </Box>
        </Box>
        <PreferencesDialog
          open={openPreference}
          setOpen={setOpenPreference}
          setIsLoading={handleLoading}
          setIsPreferenceLoading={setIsPreferenceLoading}
          handlePreferenceChange={handlePreferenceChange}
        />
        <CustomLoader isLoading={isPreferenceLoading} />
      </>
    );
  };
  return getVedazTalk();
};

export default VedazTalk;
